import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBusinessProfiles, type BusinessProfileInput } from '../../../hooks/useBusinessProfiles';
import { useUserPlan } from '../../../hooks/useUserPlan';
import { useAuth } from '../../../context/AuthContext';
import LogoUpload from './LogoUpload';

const businessTypes = [
  'Plumbing / HVAC', 'Landscaping', 'Cleaning Services', 'Restaurant / Food',
  'Fitness / Gym', 'Legal Services', 'Real Estate', 'E-commerce / Retail',
  'Healthcare', 'Education / Tutoring', 'Auto / Mechanic', 'SaaS / Software',
  'Consulting', 'Beauty / Salon', 'Other',
];

const toneOptions = [
  { value: 'professional', label: 'Professional' },
  { value: 'bold', label: 'Bold & Direct' },
  { value: 'friendly', label: 'Friendly & Casual' },
  { value: 'urgent', label: 'Urgent & Compelling' },
  { value: 'luxury', label: 'Luxury & Premium' },
  { value: 'witty', label: 'Witty & Creative' },
];

const brandPositions = [
  { value: 'affordable', label: 'Affordable & Value', icon: 'ri-price-tag-3-line' },
  { value: 'quality', label: 'Quality & Reliable', icon: 'ri-medal-line' },
  { value: 'premium', label: 'Premium & Professional', icon: 'ri-award-line' },
  { value: 'luxury', label: 'Luxury & Exclusive', icon: 'ri-vip-crown-line' },
  { value: 'innovative', label: 'Innovative & Modern', icon: 'ri-rocket-line' },
  { value: 'community', label: 'Community & Local', icon: 'ri-heart-line' },
];

const emptyForm: BusinessProfileInput = {
  business_name: '',
  business_type: '',
  business_description: '',
  website: '',
  location: '',
  services_offered: '',
  unique_differentiator: '',
  default_audience: '',
  default_tone: 'professional',
  brand_positioning: 'quality',
  logo_url: null,
};

interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function Field({ label, required, hint, children }: FieldProps) {
  return (
    <div>
      <label className="block text-xs font-semibold text-stone-700 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-orange-500 normal-case tracking-normal">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-stone-400 mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-100 p-6">
      <div className="mb-5">
        <h2 className="text-sm font-bold text-stone-900">{title}</h2>
        {description && <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{description}</p>}
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

export default function ProfileFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { profiles, loading, createProfile, updateProfile } = useBusinessProfiles();
  const { plan, profileLimit } = useUserPlan();
  const { user } = useAuth();

  const isEdit = Boolean(id);
  const [form, setForm] = useState<BusinessProfileInput>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [customBusinessType, setCustomBusinessType] = useState('');

  const isOtherSelected = form.business_type === 'Other' || (
    form.business_type !== '' && !businessTypes.includes(form.business_type)
  );

  useEffect(() => {
    if (isEdit && !loading) {
      const existing = profiles.find(p => p.id === id);
      if (existing) {
        const { id: _id, user_id: _uid, created_at: _ca, updated_at: _ua, ...rest } = existing;
        // If the saved type isn't in the predefined list, treat it as a custom "Other" value
        if (rest.business_type && !businessTypes.includes(rest.business_type)) {
          setCustomBusinessType(rest.business_type);
          setForm({ ...rest, business_type: 'Other' });
        } else {
          setForm(rest);
        }
      }
    }
  }, [isEdit, id, profiles, loading]);

  const handleChange = (field: keyof BusinessProfileInput, value: string | null) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const effectiveBusinessType = isOtherSelected ? customBusinessType.trim() : form.business_type;
  const isValid = form.business_name.trim() && effectiveBusinessType;

  const handleSubmit = async () => {
    setAttempted(true);
    if (!isValid || saving) {
      if (!isValid) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    setSaving(true);
    setError(null);
    // Save the actual custom type string, not "Other"
    const submitForm = { ...form, business_type: effectiveBusinessType };
    let err: string | null = null;
    if (isEdit && id) {
      const result = await updateProfile(id, submitForm);
      err = result.error;
    } else {
      const result = await createProfile(submitForm);
      err = result.error;
    }
    setSaving(false);
    if (err) {
      setError(err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/dashboard/profiles');
    }
  };

  const inputClass = 'w-full text-sm px-4 py-3 rounded-xl border border-stone-200 bg-stone-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-orange-400 focus:bg-white transition-all duration-200';
  const inputErrorClass = 'w-full text-sm px-4 py-3 rounded-xl border border-red-300 bg-red-50 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-red-400 focus:bg-white transition-all duration-200';
  const textareaClass = `${inputClass} resize-none`;

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <header className="h-16 bg-white border-b border-stone-100 flex items-center justify-between px-6 shrink-0 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/dashboard/profiles')}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-500 hover:text-stone-800 hover:bg-stone-100 transition-all duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" />
          </button>
          <div>
            <h1 className="text-base font-bold text-stone-900">{isEdit ? 'Edit Business Profile' : 'Create Business Profile'}</h1>
            <p className="text-xs text-stone-400">Save once, reuse in every ad campaign</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/dashboard/profiles')}
            className="text-xs font-semibold text-stone-500 hover:text-stone-700 px-3 py-2 rounded-lg hover:bg-stone-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
              !saving
                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                : 'bg-stone-100 text-stone-400 cursor-not-allowed'
            }`}
          >
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Saving...</> : <><i className="ri-check-line" /> {isEdit ? 'Save Changes' : 'Create Profile'}</>}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-6 space-y-5">
        {/* Info banner */}
        <div className="bg-orange-50 border border-orange-100 rounded-xl px-4 py-3 flex items-start gap-3">
          <i className="ri-information-line text-orange-400 text-base mt-0.5 shrink-0" />
          <p className="text-xs text-stone-600 leading-relaxed">
            <strong className="text-stone-800">This information will be reused automatically</strong> every time you generate ads for this business — no need to re-enter it each time. You only need to provide the campaign-specific details like your current offer.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-600">
            <i className="ri-error-warning-line shrink-0" />
            {error}
          </div>
        )}

        {attempted && !isValid && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-700">
            <i className="ri-alert-line shrink-0" />
            Please fill in the required fields: <strong>Business Name</strong> and <strong>Business Type</strong>.
          </div>
        )}

        {/* Section 1: Business Identity */}
        <Section title="Business Identity" description="Core details about the business">
          {user && (
            <Field
              label="Business Logo"
              hint="Upload your logo to use as a visual reference during ad and social post generation (optional)"
            >
              <LogoUpload
                userId={user.id}
                currentLogoUrl={form.logo_url}
                onLogoChange={(url) => handleChange('logo_url' as keyof BusinessProfileInput, url)}
              />
            </Field>
          )}
          <Field label="Business Name" required>
            <input
              type="text"
              value={form.business_name}
              onChange={e => handleChange('business_name', e.target.value)}
              placeholder="e.g. Rivera Plumbing Co."
              className={attempted && !form.business_name.trim() ? inputErrorClass : inputClass}
            />
            {attempted && !form.business_name.trim() && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line" /> Business name is required
              </p>
            )}
          </Field>

          <Field label="Business Type / Industry" required>
            <select
              value={form.business_type}
              onChange={e => {
                handleChange('business_type', e.target.value);
                if (e.target.value !== 'Other') setCustomBusinessType('');
              }}
              className={`${attempted && !effectiveBusinessType ? inputErrorClass : inputClass} cursor-pointer appearance-none`}
            >
              <option value="">Select your industry...</option>
              {businessTypes.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            {isOtherSelected && (
              <div className="mt-2">
                <input
                  type="text"
                  value={customBusinessType}
                  onChange={e => setCustomBusinessType(e.target.value)}
                  placeholder="Describe your industry or business type..."
                  maxLength={80}
                  className={attempted && !customBusinessType.trim() ? inputErrorClass : inputClass}
                  autoFocus
                />
                {attempted && !customBusinessType.trim() && (
                  <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                    <i className="ri-error-warning-line" /> Please describe your business type
                  </p>
                )}
              </div>
            )}
            {attempted && !effectiveBusinessType && !isOtherSelected && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line" /> Please select your industry
              </p>
            )}
          </Field>

          <Field
            label="Business Description"
            hint="Describe what makes your business unique. The more detail you add, the better your ads will be."
          >
            <textarea
              value={form.business_description}
              onChange={e => handleChange('business_description', e.target.value)}
              placeholder="e.g. Family-owned plumbing company serving Austin since 2005. Known for fast response times and transparent pricing. Specialize in emergency repairs and bathroom remodels."
              rows={4}
              maxLength={600}
              className={textareaClass}
            />
            <div className="text-right text-xs text-stone-400 mt-1">{form.business_description.length}/600</div>
          </Field>

          <Field
            label="What Makes This Business Different"
            hint="Your key competitive advantage — why customers choose you over competitors. This will be woven into your ad copy."
          >
            <textarea
              value={form.unique_differentiator}
              onChange={e => handleChange('unique_differentiator', e.target.value)}
              placeholder="e.g. We offer a 100% satisfaction guarantee, arrive within 60 minutes, and never charge overtime — even on weekends and holidays."
              rows={3}
              maxLength={400}
              className={textareaClass}
            />
            <div className="text-right text-xs text-stone-400 mt-1">{(form.unique_differentiator ?? '').length}/400</div>
          </Field>
        </Section>

        {/* Section 2: Business Details */}
        <Section title="Business Details" description="Location, website, and services">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Website" hint="Optional — used to add credibility to ad copy">
              <input
                type="text"
                value={form.website}
                onChange={e => handleChange('website', e.target.value)}
                placeholder="e.g. riveraplumbing.com"
                className={inputClass}
              />
            </Field>
            <Field label="Location / Service Area" hint="City, region, or 'nationwide'">
              <input
                type="text"
                value={form.location}
                onChange={e => handleChange('location', e.target.value)}
                placeholder="e.g. Austin, TX or Greater Boston"
                className={inputClass}
              />
            </Field>
          </div>

          <Field
            label="Services Offered"
            hint="List your main services or products, separated by commas"
          >
            <textarea
              value={form.services_offered}
              onChange={e => handleChange('services_offered', e.target.value)}
              placeholder="e.g. Emergency plumbing repairs, drain cleaning, water heater installation, bathroom remodels"
              rows={3}
              maxLength={400}
              className={textareaClass}
            />
            <div className="text-right text-xs text-stone-400 mt-1">{form.services_offered.length}/400</div>
          </Field>
        </Section>

        {/* Section 3: Ad Preferences */}
        <Section title="Ad Generation Preferences" description="These defaults will be used in every ad generation but can be overridden per campaign">
          <Field
            label="Default Target Audience"
            hint="Who are your typical customers? Be specific for better results."
          >
            <input
              type="text"
              value={form.default_audience}
              onChange={e => handleChange('default_audience', e.target.value)}
              placeholder="e.g. Homeowners in Austin aged 30–55 who own property"
              className={inputClass}
            />
          </Field>

          <Field label="Default Tone of Voice">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {toneOptions.map(t => (
                <button
                  key={t.value}
                  onClick={() => handleChange('default_tone', t.value)}
                  className={`py-2.5 px-3 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap text-left ${
                    form.default_tone === t.value
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Brand Positioning" hint="How do you want your brand to come across?">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {brandPositions.map(p => (
                <button
                  key={p.value}
                  onClick={() => handleChange('brand_positioning', p.value)}
                  className={`flex items-center gap-2 py-2.5 px-3 rounded-xl text-xs font-semibold border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    form.brand_positioning === p.value
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-500'
                  }`}
                >
                  <i className={`${p.icon} text-xs shrink-0`} />
                  {p.label}
                </button>
              ))}
            </div>
          </Field>
        </Section>

        {/* Submit */}
        <div className="flex flex-col gap-3 pb-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-600">
              <i className="ri-error-warning-line shrink-0" />
              {error}
            </div>
          )}
          {attempted && !isValid && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-700">
              <i className="ri-alert-line shrink-0" />
              Please fill in the required fields highlighted above.
            </div>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => navigate('/dashboard/profiles')}
              className="text-sm font-semibold text-stone-500 hover:text-stone-700 px-5 py-3 rounded-xl hover:bg-stone-100 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center gap-2 text-sm font-bold px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap ${
                !saving
                  ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500 cursor-pointer'
                  : 'bg-stone-100 text-stone-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin" /> Saving...</>
              ) : (
                <><i className="ri-check-line" /> {isEdit ? 'Save Changes' : 'Create Business Profile'}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
